import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { X, MessageSquare, Phone, Clock, CheckCircle } from "lucide-react";

export default function SMSLogPanel({ isOpen, onClose }) {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchLogs();
    }
  }, [isOpen]);

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/sms-logs");
      setLogs(response.data);
    } catch (error) {
      // If not employer, try personal logs
      try {
        const response = await apiClient.get("/sms-logs/my");
        setLogs(response.data);
      } catch (e) {
        console.error("Failed to fetch SMS logs:", e);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid="sms-log-panel">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-indigo-900" />
              <h2 className="font-heading font-bold text-lg text-slate-900">
                SMS Alerts Log
              </h2>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              data-testid="close-sms-log-button"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-slate-500 text-sm">
            Mock SMS alerts sent by the system
          </p>
          <Badge variant="outline" className="mt-2 text-xs">
            Demo Mode - No real SMS sent
          </Badge>
        </div>

        {/* SMS List */}
        <ScrollArea className="h-[calc(100vh-140px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-900"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No SMS alerts yet</p>
              <p className="text-slate-400 text-sm mt-1">SMS alerts will appear here when rides are updated</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="p-4 bg-slate-50 rounded-xl border border-slate-100"
                  data-testid={`sms-log-${log.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-indigo-600" />
                      <span className="font-mono text-sm font-medium text-slate-900">
                        {log.phone_number}
                      </span>
                    </div>
                    <Badge className="bg-green-100 text-green-700 text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {log.status}
                    </Badge>
                  </div>
                  <p className="text-slate-700 text-sm mb-2">{log.message}</p>
                  <div className="flex items-center gap-1 text-slate-400 text-xs">
                    <Clock className="w-3 h-3" />
                    {formatTime(log.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
